import { compact, keyBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { TypeaheadMultiSelectInput } from '@/components/form/baseInputs/TypeaheadMultiSelectInput/TypeaheadMultiSelectInput';
import { useFormContext } from '@/components/react-hook-form';
import {
  DispositiveProvisionsFormRecipient,
  RecipientKind,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import {
  _getDefaultedKindForRecipient,
  GetDefaultedKindForRecipientParams,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/hooks/useDefaultRecipientTransferTaxKind';
import { SURVIVING_SPOUSE_SENTINEL } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/useRecipientOptions';
import {
  SelectedRecipient,
  SplitDispositionFormShape,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/SplitDispositionModal/SplitDispositionModal.types';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { DispositiveProvisionTransferTaxKind } from '@/types/schema';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { useBdgDyingClientId } from '../../hooks/useBdgDyingClientId';
import { useBeneficiaryDesignationRecipientOptions } from '../../hooks/useBeneficiaryDesignationRecipientOptions';

interface BdgSplitRecipientsSelectorProps {
  errorMessage?: string;
  entityIdOverride?: string;
  isOnHypotheticalWaterfall: boolean;
}

function getRecipientKindFromId(recipientId: string): RecipientKind {
  if (recipientId === SURVIVING_SPOUSE_SENTINEL) {
    return RecipientKind.SurvivingSpouse;
  }

  const pulidKind = getPulidKind(recipientId);
  switch (pulidKind) {
    case PulidKind.ClientProfile:
      return RecipientKind.Individual;
    case PulidKind.Entity:
      return RecipientKind.Entity;
    case PulidKind.ClientOrganization:
      return RecipientKind.Organization;
    case PulidKind.TestamentaryEntity:
      return RecipientKind.TestamentaryEntity;
    default:
      return RecipientKind.Individual;
  }
}

/**
 * Recipient multi-select for the beneficiary-designations split modal. Mirrors
 * the DP `SplitRecipientsSelector`: sources options (grouped by recipient
 * category) from the BDG recipient hook and auto-defaults each new recipient's
 * type from its relationship to the decedent, matching the BDG recipient card.
 */
export function BdgSplitRecipientsSelector({
  errorMessage,
  entityIdOverride,
  isOnHypotheticalWaterfall,
}: BdgSplitRecipientsSelectorProps) {
  const { control, setValue } = useFormContext<SplitDispositionFormShape>();
  const watchedRecipients = useWatch({ control, name: 'selectedRecipients' });
  const selectedRecipients = useMemo(
    () => watchedRecipients ?? [],
    [watchedRecipients]
  );

  const { options, typeaheadOptions, recipientLists } =
    useBeneficiaryDesignationRecipientOptions({
      entityIdOverride,
      isOnHypotheticalWaterfall,
    });

  const { primaryClients } = useHouseholdDetailsContext();
  const dyingClientId = useBdgDyingClientId();

  const getDisplayName = useCallback(
    (value: string): string =>
      options.find((o) => o.value === value)?.display ?? value,
    [options]
  );

  const defaultTransferTaxKindParams: GetDefaultedKindForRecipientParams =
    useMemo(
      () => ({
        ...recipientLists,
        primaryClients: primaryClients ?? [],
        dyingClientId: dyingClientId ?? '',
        hasStateTax: false,
      }),
      [recipientLists, primaryClients, dyingClientId]
    );

  const selectedIds = useMemo(
    () => selectedRecipients.map((r) => r.id),
    [selectedRecipients]
  );
  const selectedOptions = useMemo(() => {
    const optionsByValue = keyBy(typeaheadOptions, 'value');
    return compact(selectedIds.map((id) => optionsByValue[id]));
  }, [selectedIds, typeaheadOptions]);

  const handleChange = useCallback(
    (_event: unknown, newOptions: Array<{ value: string }>) => {
      const newSelectedIds = newOptions.map((option) => option.value);

      const updatedRecipients: SelectedRecipient[] = newSelectedIds.map(
        (id) => {
          // Preserve existing recipients (keeps any recipient-type edits)
          const existing = selectedRecipients.find((r) => r.id === id);
          if (existing) return existing;

          // Auto-populate the recipient type from its relationship to the
          // decedent, matching the DP split selector and BDG recipient card.
          const defaultKind = _getDefaultedKindForRecipient(
            { recipientId: id } as DispositiveProvisionsFormRecipient,
            defaultTransferTaxKindParams
          );

          return {
            id,
            displayName: getDisplayName(id),
            kind: getRecipientKindFromId(id),
            transferTaxKind:
              defaultKind ?? ('' as DispositiveProvisionTransferTaxKind | ''),
          };
        }
      );

      setValue('selectedRecipients', updatedRecipients);
    },
    [defaultTransferTaxKindParams, getDisplayName, selectedRecipients, setValue]
  );
  const [inputValue, setInputValue] = useState('');

  return (
    <TypeaheadMultiSelectInput
      label="Recipient"
      hideLabel
      values={selectedOptions}
      options={typeaheadOptions}
      onChange={handleChange}
      errorMessage={errorMessage}
      placeholder="Search recipients"
      inputValue={inputValue}
      onInputChange={setInputValue}
    />
  );
}
